import React, { useEffect, useState } from "react";
import Datatables from 'pages/utils/table/datatable';
import { PATIENT_URL } from "helpers/url_helper";
import { get, del } from "helpers/api_helper";
import { useHistory, Link } from "react-router-dom";
import moment from "moment";
import {
  RiUserHeartLine,
  RiEyeLine,
  RiEditLine,
  RiDeleteBinLine,
  RiWhatsappLine,
  RiPhoneLine,
  RiMailLine,
  RiMapPinLine,
  RiShieldCheckLine,
  RiCalendarEventLine
} from "react-icons/ri";
import { showSuccessAlert, showErroeAlert, showDeleteConfirmation } from "pages/utils/alertMessages";
import { encodePatientId } from "helpers/url_crypto_helper";
import { CardBody, Card } from "reactstrap";

const Patientslist = ({ onEditPatient, refreshTrigger }) => {
  const history = useHistory();
  const [dataRows, setDataRows] = useState({ totalItems: 0, items: [] });
  const [loading, setLoading] = useState(false);
  const [options, setOptions] = useState(() => {
    try {
      const cached = sessionStorage.getItem('options');
      return cached ? JSON.parse(cached) : { allergies: [], tags: [] };
    } catch (e) {
      return { allergies: [], tags: [] };
    }
  });

  useEffect(() => {
    get(`${PATIENT_URL}/profile/options`)
      .then(res => {
        if (res?.success && res?.body) {
          sessionStorage.setItem('options', JSON.stringify(res.body));
          setOptions(res.body);
        }
      })
      .catch(() => { });
  }, []);

  const getAllergyTitle = (item) => {
    if (!item && item !== 0) return '';
    if (typeof item === 'object') return item.label || item.title || item.name || item.value || '';
    const found = (options?.allergies || []).find(o =>
      String(o.value) === String(item) || String(o.id) === String(item) || String(o.label).toLowerCase() === String(item).toLowerCase()
    );
    return found ? (found.label || found.title || found.name) : String(item);
  };

  const getTagTitle = (item) => {
    if (!item && item !== 0) return '';
    if (typeof item === 'object') return item.label || item.title || item.name || item.value || '';
    const found = (options?.tags || []).find(o =>
      String(o.value) === String(item) || String(o.id) === String(item) || String(o.label).toLowerCase() === String(item).toLowerCase()
    );
    return found ? (found.label || found.title || found.name) : String(item);
  };

  const [pagination, setPagination] = useState({
    limit: 10,
    page: 1,
    search: ''
  });

  const getPatients = async () => {
    setLoading(true);
    try {
      const searchVal = pagination.search || pagination.Search || pagination.q || pagination.searchTerm || '';
      const limitVal = pagination.limit || pagination.sizePerPage || 10;
      const pageVal = pagination.page || 1;
      const { success, body } = await get(`${PATIENT_URL}?limit=${limitVal}&page=${pageVal}&q=${encodeURIComponent(searchVal)}`);
      if (success) {
        setDataRows(body || { totalItems: 0, items: [] });
      }
    } catch (err) {
      console.error("Failed to load patients:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getPatients();
  }, [pagination, refreshTrigger]);

  const handleDelete = (id, status) => {
    showDeleteConfirmation(async () => {
      const res = await del(`${PATIENT_URL}/${id}?status=${status}`);
      if (res?.success) {
        showSuccessAlert("Patient record updated successfully.");
        getPatients();
      } else {
        showErroeAlert("Unable to update patient status.");
      }
    });
  };

  const columns = [
    {
      dataField: 'id',
      text: '#',
      headerStyle: { width: '50px', textAlign: 'center' },
      style: { textAlign: 'center', width: '50px' },
      formatter: (cell, row, rowIndex) => (
        <span className="text-muted font-weight-semibold font-size-12">
          {(pagination?.page - 1) * pagination?.limit + (rowIndex + 1)}
        </span>
      )
    },
    {
      dataField: 'name',
      text: 'Patient Profile',
      formatter: (cell, row) => {
        const fullName = `${row.title || ''} ${row.first_name || ''} ${row.last_name || ''}`.trim() || 'Unnamed Patient';
        const initials = `${row.first_name?.[0] || ''}${row.last_name?.[0] || ''}`.toUpperCase() || 'P';

        // Color hash based on name
        const colors = ['#F8CA15', '#059669', '#7c3aed', '#d97706', '#dc2626', '#0891b2'];
        const colorIdx = (row.id || 1) % colors.length;
        const avatarBg = colors[colorIdx];

        return (
          <div className="patient-profile-cell py-1">
            <div
              className="patient-avatar-circle"
              style={{ backgroundColor: avatarBg }}
            >
              {initials}
            </div>
            <div>
              <div className="d-flex align-items-center gap-2 flex-wrap">
                <Link
                  to={`/patient?_p=${row.id}`}
                  className="patient-name-link font-weight-bold"
                  title="Open Clinical EMR Chart"
                >
                  {fullName}
                </Link>
                <span className="patient-case-badge">
                  {row.case_no || `#${row.id}`}
                </span>
              </div>
              <div className="d-flex align-items-center gap-2 mt-1">
                {row.gender && (
                  <span className={`badge ${row.gender.toLowerCase() === 'female' ? 'bg-pink-subtle text-pink' : 'bg-primary-subtle text-primary'} font-size-11`}>
                    {row.gender}
                  </span>
                )}
                {row.age > 0 && (
                  <span className="text-muted font-size-11">
                    {row.age} yrs
                  </span>
                )}
                {row.blood_group && (
                  <span className="badge bg-danger-subtle text-danger font-size-10 border border-danger-subtle">
                    🩸 {row.blood_group}
                  </span>
                )}
              </div>
            </div>
          </div>
        );
      }
    },
    {
      dataField: 'contact',
      text: 'Contact Details',
      isDummyField: true,
      formatter: (cell, row) => (
        <div className="patient-contact-box py-1">
          {row.mobile && (
            <div className="d-flex align-items-center gap-2">
              <span className="patient-contact-item">
                <RiPhoneLine className="text-primary font-size-13" />
                <strong className="text-slate-800">{row.mobile}</strong>
              </span>
              <a
                href={`https://wa.me/${row.mobile.replace(/[^0-9]/g, '')}?text=${encodeURIComponent(`Hello ${row.first_name}, greeting from 909 Dental OPD.`)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="patient-wa-btn"
                title="Send WhatsApp Message"
              >
                <RiWhatsappLine /> WhatsApp
              </a>
            </div>
          )}
          {row.email && (
            <div className="patient-contact-item mt-1">
              <RiMailLine className="text-muted font-size-12" />
              <a href={`mailto:${row.email}`} className="text-muted text-truncate" style={{ maxWidth: '160px' }}>
                {row.email}
              </a>
            </div>
          )}
        </div>
      )
    },
    {
      dataField: 'location',
      text: 'City & Address',
      isDummyField: true,
      formatter: (cell, row) => (
        <div className="py-1">
          {row.city ? (
            <div className="d-flex align-items-center gap-1 text-slate-800 font-weight-semibold font-size-12">
              <RiMapPinLine className="text-danger font-size-13" />
              <span>{row.city}{row.state ? `, ${row.state}` : ''}</span>
            </div>
          ) : (
            <span className="text-muted font-size-11">—</span>
          )}
          {row.address && (
            <div className="text-muted font-size-11 text-truncate mt-0.5" style={{ maxWidth: '180px' }} title={row.address}>
              {row.address}
            </div>
          )}
        </div>
      )
    },
    {
      dataField: 'medical_tags',
      text: 'Medical Notes & Tags',
      isDummyField: true,
      formatter: (cell, row) => {
        const tags = typeof row.patient_tags === 'string' && row.patient_tags.startsWith('[')
          ? JSON.parse(row.patient_tags)
          : (row.patient_tags ? row.patient_tags.split(',') : []);

        const allergies = typeof row.allergies === 'string' && row.allergies.startsWith('[')
          ? JSON.parse(row.allergies)
          : (row.allergies ? row.allergies.split(',') : []);

        if (tags.length === 0 && allergies.length === 0) {
          return <span className="text-muted font-size-11 font-italic">No medical alerts</span>;
        }

        return (
          <div className="d-flex flex-wrap gap-1 py-1">
            {allergies.map((a, i) => (
              <span key={`al-${i}`} className="badge bg-danger-subtle text-danger font-size-10 border border-danger-subtle" title="Allergy">
                ⚠️ {getAllergyTitle(a)}
              </span>
            ))}
            {tags.map((t, i) => (
              <span key={`tg-${i}`} className="badge bg-light text-slate-700 border font-size-10">
                🏷️ {getTagTitle(t)}
              </span>
            ))}
          </div>
        );
      }
    },
    {
      dataField: 'date',
      text: 'Registered On',
      formatter: (cell, row) => {
        const regDate = cell || row?.date || row?.created_at || row?.createdAt;
        return (
          <span className="text-muted font-size-12">
            {regDate ? moment(regDate).format('DD MMM YYYY') : '—'}
          </span>
        );
      }
    },
    {
      dataField: 'actions',
      text: 'Actions',
      headerStyle: { textAlign: 'center', width: '130px' },
      style: { textAlign: 'center', width: '130px' },
      isDummyField: true,
      formatter: (cell, row) => (
        <div className="patient-actions-cell justify-content-center py-1">
          <Link
            to={`/patient?_p=${row.id}`}
            className="patient-action-btn view me-1"
            title="Open Complete Patient EMR Chart"
          >
            <RiEyeLine />
          </Link>
          <button
            type="button"
            className="patient-action-btn edit me-1"
            onClick={() => onEditPatient && onEditPatient(row)}
            title="Edit Patient Details"
          >
            <RiEditLine />
          </button>
          <button
            type="button"
            className="patient-action-btn delete me-1"
            onClick={() => handleDelete(row.id, row.status === 0 ? 1 : 0)}
            title={row.status === 0 ? 'Deactivate / Delete' : 'Restore'}
          >
            <RiDeleteBinLine />
          </button>
        </div>
      )
    }
  ];

  return (
    <div className="patient-datatable-wrapper">
      <Card>
        <CardBody>
          <Datatables
            isSearch={true}
            columns={columns}
            showTableOnly={true}
            rowsLength={dataRows?.totalItems || 0}
            rows={dataRows.items || []}
            keyField={'id'}
            title="Registered Dental Patients"
            isAdd={false}
            isTableHead={true}
            loading={loading}
            ssr={setPagination}
          />
        </CardBody>
      </Card>
    </div>
  );
};

export default Patientslist;